/**
 * PeerEvaluationCreateModal Component
 *
 * Modal dialog for creating a new peer evaluation request.
 * Allows manager/HR to select an evaluator and which tasks/competencies to include.
 *
 * Story pe-2-2
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useEmployees } from '../employees/useEmployees';
import { useEmployeeAssignedTasks } from '../employees/useEmployeeTasks';
import { useTaskPeriods } from '../employees/useAssignmentPeriods';
import { useEmployeeCompetencies } from '../employees/useEmployeeCompetencies';
import { useCreatePeerEvaluation } from './usePeerEvaluations';

interface PeerEvaluationCreateModalProps {
  /** ID of the employee being evaluated */
  employeeId: number;
  /** Employee name for display purposes */
  employeeName?: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when peer evaluation is successfully created */
  onSuccess: () => void;
}

interface FormState {
  evaluatorId: number | null;
  taskIds: number[];
  competencyIds: number[];
}

interface FormErrors {
  evaluator?: string;
  selection?: string;
}

const FOCUS_DELAY_MS = 100;

export function PeerEvaluationCreateModal({
  employeeId,
  employeeName,
  isOpen,
  onClose,
  onSuccess,
}: PeerEvaluationCreateModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLSelectElement>(null);

  // Form state
  const [formState, setFormState] = useState<FormState>({
    evaluatorId: null,
    taskIds: [],
    competencyIds: [],
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Data hooks
  const { data: employeesData, isLoading: loadingEmployees } = useEmployees();
  // Fetch only the tasks and competencies assigned to this specific employee
  const { data: employeeTasksData, isLoading: loadingTasks } = useEmployeeAssignedTasks(employeeId);
  const { data: employeeCompetenciesData, isLoading: loadingCompetencies } = useEmployeeCompetencies(employeeId);
  // Epic 4 (Story 4.5, AC #2, AD-12) — een core_tasks_v2-medewerker heeft
  // tijdvakken (assignment_periods); `taskIds` hieronder worden voor zo'n
  // medewerker geïnterpreteerd als kerntaak-id's (zelfde wire-shape-hergebruik
  // als de backend, zie peerEvaluationService.ts create()-dev-notes).
  const { data: taskPeriods } = useTaskPeriods(employeeId);

  // Mutation hook
  const createMutation = useCreatePeerEvaluation();

  // Gefilterde medewerkers lijst (exclusief de medewerker die beoordeeld wordt)
  const availableEvaluators = useMemo(
    () => employeesData?.data?.filter((emp) => emp.id !== employeeId) ?? [],
    [employeesData?.data, employeeId]
  );

  const isCoreTasksV2Employee = (taskPeriods?.length ?? 0) > 0;

  // Get tasks assigned to this employee — kerntaken (v2) of taken (v1),
  // genormaliseerd naar dezelfde {id, name, beschrijving}-vorm die de
  // picker hieronder al gebruikte.
  const tasks = useMemo(() => {
    if (isCoreTasksV2Employee) {
      const seen = new Set<number>();
      const result: Array<{ id: number; name: string; beschrijving?: string | null }> = [];
      for (const period of taskPeriods ?? []) {
        for (const assignment of period.assignments) {
          if (seen.has(assignment.coreTaskId)) continue;
          seen.add(assignment.coreTaskId);
          result.push({ id: assignment.coreTaskId, name: assignment.coreTaskName, beschrijving: `Domein: ${assignment.domain}` });
        }
      }
      return result;
    }
    return employeeTasksData?.tasks ?? [];
  }, [isCoreTasksV2Employee, taskPeriods, employeeTasksData?.tasks]);
  // Combine basis and vak competencies for this employee
  const competencies = useMemo(() => {
    if (!employeeCompetenciesData) return [];
    return [...(employeeCompetenciesData.basis ?? []), ...(employeeCompetenciesData.vak ?? [])];
  }, [employeeCompetenciesData]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormState({
        evaluatorId: null,
        taskIds: [],
        competencyIds: [],
      });
      setErrors({});
      setHasAttemptedSubmit(false);
      // Focus eerste veld wanneer modal opent
      setTimeout(() => firstFocusableRef.current?.focus(), FOCUS_DELAY_MS);
    }
  }, [isOpen]);

  // Focus trap en escape toets handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      // Focus trap
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button:not([disabled]), select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Valideer formulier
  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formState.evaluatorId) {
      newErrors.evaluator = 'Selecteer een collega als evaluator';
    } else if (formState.evaluatorId === employeeId) {
      newErrors.evaluator = 'De evaluator kan niet dezelfde persoon zijn als de medewerker';
    }

    if (formState.taskIds.length === 0 && formState.competencyIds.length === 0) {
      newErrors.selection = 'Selecteer minimaal 1 taak of 1 competentie';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState, employeeId]);

  // Voer validatie uit bij form wijziging na eerste submit poging
  useEffect(() => {
    if (hasAttemptedSubmit) {
      validate();
    }
  }, [formState, hasAttemptedSubmit, validate]);

  // Event handlers
  const handleEvaluatorChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormState((prev) => ({
      ...prev,
      evaluatorId: value ? parseInt(value, 10) : null,
    }));
  }, []);

  const handleTaskToggle = useCallback((taskId: number) => {
    setFormState((prev) => ({
      ...prev,
      taskIds: prev.taskIds.includes(taskId)
        ? prev.taskIds.filter((id) => id !== taskId)
        : [...prev.taskIds, taskId],
    }));
  }, []);

  const handleTaskSelectAll = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      taskIds: prev.taskIds.length === tasks.length ? [] : tasks.map((t) => t.id),
    }));
  }, [tasks]);

  const handleCompetencyToggle = useCallback((competencyId: number) => {
    setFormState((prev) => ({
      ...prev,
      competencyIds: prev.competencyIds.includes(competencyId)
        ? prev.competencyIds.filter((id) => id !== competencyId)
        : [...prev.competencyIds, competencyId],
    }));
  }, []);

  const handleCompetencySelectAll = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      competencyIds:
        prev.competencyIds.length === competencies.length
          ? []
          : competencies.map((c) => c.id),
    }));
  }, [competencies]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);

    if (!validate()) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        employeeId,
        evaluatorId: formState.evaluatorId!,
        taskIds: formState.taskIds,
        competencyIds: formState.competencyIds,
      });
      onSuccess();
      onClose();
    } catch {
      // Error wordt afgehandeld door React Query error state (createMutation.isError)
    }
  };

  if (!isOpen) return null;

  const isLoading = loadingEmployees || loadingTasks || loadingCompetencies;
  const isSubmitting = createMutation.isPending;
  const allTasksSelected = tasks.length > 0 && formState.taskIds.length === tasks.length;
  const allCompetenciesSelected =
    competencies.length > 0 && formState.competencyIds.length === competencies.length;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="peer-evaluation-modal-title"
      role="dialog"
      aria-modal="true"
      data-testid="peer-evaluation-modal"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-500/75 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
        data-testid="modal-backdrop"
      />

      {/* Modal positioning */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2
              id="peer-evaluation-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              Peer Evaluatie Aanmaken
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label="Sluiten"
              data-testid="modal-close-button"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Info banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  Maak een peer evaluatie aan voor{' '}
                  <span className="font-medium">{employeeName || `medewerker #${employeeId}`}</span>.
                  De evaluator ontvangt een unieke link om feedback te geven.
                </p>
              </div>

              {/* Loading state */}
              {isLoading && (
                <div className="flex items-center justify-center py-8" data-testid="loading-spinner">
                  <svg
                    className="animate-spin h-8 w-8 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              )}

              {!isLoading && (
                <>
                  {/* Evaluator Selection */}
                  <div>
                    <label
                      htmlFor="evaluator"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Evaluator *
                    </label>
                    <select
                      ref={firstFocusableRef}
                      id="evaluator"
                      value={formState.evaluatorId ?? ''}
                      onChange={handleEvaluatorChange}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                        errors.evaluator ? 'border-red-300' : 'border-gray-300'
                      }`}
                      data-testid="evaluator-select"
                    >
                      <option value="">Selecteer een collega</option>
                      {availableEvaluators.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                    {errors.evaluator && (
                      <p
                        className="mt-1 text-sm text-red-600"
                        data-testid="evaluator-error"
                        role="alert"
                        aria-live="polite"
                      >
                        {errors.evaluator}
                      </p>
                    )}
                  </div>

                  {/* Tasks Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Taken ({formState.taskIds.length}/{tasks.length})
                      </label>
                      {tasks.length > 0 && (
                        <button
                          type="button"
                          onClick={handleTaskSelectAll}
                          disabled={isSubmitting}
                          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                          data-testid="select-all-tasks"
                        >
                          {allTasksSelected ? 'Deselecteer alles' : 'Selecteer alles'}
                        </button>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {tasks.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">Deze medewerker heeft geen toegewezen taken</p>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {tasks.map((task) => (
                            <label
                              key={task.id}
                              className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formState.taskIds.includes(task.id)}
                                onChange={() => handleTaskToggle(task.id)}
                                disabled={isSubmitting}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`task-checkbox-${task.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{task.name}</p>
                                {task.beschrijving && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {task.beschrijving}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Competencies Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Competenties ({formState.competencyIds.length}/{competencies.length})
                      </label>
                      {competencies.length > 0 && (
                        <button
                          type="button"
                          onClick={handleCompetencySelectAll}
                          disabled={isSubmitting}
                          className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                          data-testid="select-all-competencies"
                        >
                          {allCompetenciesSelected ? 'Deselecteer alles' : 'Selecteer alles'}
                        </button>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                      {competencies.length === 0 ? (
                        <p className="p-3 text-sm text-gray-500">Deze medewerker heeft geen competenties</p>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {competencies.map((comp) => (
                            <label
                              key={comp.id}
                              className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={formState.competencyIds.includes(comp.id)}
                                onChange={() => handleCompetencyToggle(comp.id)}
                                disabled={isSubmitting}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`competency-checkbox-${comp.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-medium text-gray-900">{comp.name}</p>
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      comp.type === 'basis'
                                        ? 'bg-gray-100 text-gray-700'
                                        : 'bg-blue-100 text-blue-700'
                                    }`}
                                  >
                                    {comp.type}
                                  </span>
                                </div>
                                {comp.beschrijving && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {comp.beschrijving}
                                  </p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Selection error */}
                  {errors.selection && (
                    <p
                      className="text-sm text-red-600"
                      data-testid="selection-error"
                      role="alert"
                      aria-live="polite"
                    >
                      {errors.selection}
                    </p>
                  )}

                  {/* API error */}
                  {createMutation.isError && (
                    <div
                      className="bg-red-50 border border-red-200 rounded-lg p-3"
                      role="alert"
                      aria-live="assertive"
                    >
                      <p className="text-sm text-red-700" data-testid="api-error">
                        Er is een fout opgetreden bij het aanmaken van de peer evaluatie. Probeer
                        het opnieuw.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                data-testid="cancel-button"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={isLoading || isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="submit-button"
              >
                {isSubmitting && (
                  <>
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="sr-only">Bezig met aanmaken...</span>
                  </>
                )}
                {isSubmitting ? 'Aanmaken...' : 'Aanmaken'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
